import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";

const DesignFinishSelector = ({
  selectedFinish,
  onSelectionChange,
  onContinue,
  onBack,
}) => {
  const defaultSelections = {
    wallFinish: "stainless",
    floorType: "anti-skid-pvc",
    handrailType: "round",
    lighting: "led-spots",
    controlPanel: "touch-panel", // Changed default
  };
  const [selections, setSelections] = useState(() => {
    // If selectedFinish is already an object, use it directly
    if (selectedFinish && typeof selectedFinish === "object") {
      return {
        ...defaultSelections,
        ...selectedFinish,
      };
    }
    // Otherwise use defaults
    return defaultSelections;
  });

  const [activeTab, setActiveTab] = useState("walls");
  const prevSelectionsRef = useRef(selections);

  // Add window size tracking for responsive design
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Define responsive breakpoints
  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth <= 1024 && windowWidth > 768;
  const isSmallMobile = windowWidth <= 480;

  useEffect(() => {
    if (selectedFinish && typeof selectedFinish === "object") {
      setSelections((prevSelections) => ({
        ...prevSelections,
        ...selectedFinish,
      }));
    }
  }, [selectedFinish]);

  // At line 88, change onSelect to onSelectionChange
  // Change this section (around line 88) where the data is sent to the parent component
  useEffect(() => {
    // Only update if selections have changed
    if (
      JSON.stringify(prevSelectionsRef.current) !== JSON.stringify(selections)
    ) {
      prevSelectionsRef.current = selections;

      // Send data in the format expected by ConfigSummary
      if (onSelectionChange) {
        onSelectionChange({
          // Use the exact same property names expected by ConfigSummary
          ceilingDesign: selections.ceilingDesign,
          wallFinish: selections.wallFinish,
          floorType: selections.floorType,
          lighting: selections.lighting,
          controlPanel: selections.controlPanel,
          handrailType: selections.handrailType,
          handrailFinish: selections.handrailFinish,
          wallLayout: selections.wallLayout,
          floorPattern: selections.floorPattern,
          mirror: selections.mirror,
          display: selections.display,
          ventilation: selections.ventilation,
          kickPlates: selections.kickPlates,
          controlFinish: selections.controlFinish,
        });
      }
    }
  }, [selections, onSelectionChange]); // Changed dependency from onSelect to onSelectionChange

  const handleSelectionChange = (category, option) => {
    setSelections((prev) => ({
      ...prev,
      [category]: option,
    }));
  };

  // Get responsive styles based on screen width
  const getResponsiveStyles = () => {
    return {
      container: {
        padding: isSmallMobile ? "15px 10px" : isMobile ? "20px 15px" : "20px",
      },
      header: {
        marginBottom: isSmallMobile ? "20px" : "30px",
      },
      title: {
        fontSize: isSmallMobile ? "22px" : isMobile ? "24px" : "28px",
        marginBottom: isSmallMobile ? "4px" : "6px",
      },
      subtitle: {
        fontSize: isSmallMobile ? "14px" : "16px",
      },
      tabsContainer: {
        flexDirection: isSmallMobile ? "column" : "row",
        borderRadius: isSmallMobile ? "10px" : "12px",
        marginBottom: isSmallMobile ? "20px" : "25px",
      },
      tabButton: {
        display: "flex",
        flexDirection: isSmallMobile ? "row" : "column",
        alignItems: "center",
        justifyContent: "center",

        padding: isSmallMobile ? "12px 8px" : "16px 10px",
        width: isSmallMobile ? "100%" : "auto",
        flex: isSmallMobile ? "0 0 50%" : "1",
        borderRadius: isSmallMobile ? "0" : undefined,
      },
      mainContent: {
        padding: isSmallMobile ? "15px" : isMobile ? "20px" : "30px",
      },
      sectionTitle: {
        fontSize: isSmallMobile ? "18px" : isMobile ? "20px" : "22px",
        marginBottom: isSmallMobile ? "6px" : "8px",
      },
      sectionDescription: {
        fontSize: isSmallMobile ? "14px" : "15px",
        marginBottom: isSmallMobile ? "18px" : "25px",
      },
      optionsGrid: {
        gridTemplateColumns: isSmallMobile
          ? "1fr"
          : isMobile
          ? "repeat(2, 1fr)"
          : "repeat(auto-fill, minmax(240px, 1fr))",
        gap: isSmallMobile ? "15px" : "25px",
      },
      designVisual: {
        height: isSmallMobile ? "120px" : isMobile ? "140px" : "160px",
      },
      designInfo: {
        padding: isSmallMobile ? "15px" : "18px",
      },
      designTitle: {
        fontSize: isSmallMobile ? "15px" : "16px",
        marginBottom: isSmallMobile ? "8px" : "10px",
      },
      designDescription: {
        fontSize: isSmallMobile ? "13px" : "14px",
      },
      subSection: {
        padding: isSmallMobile ? "15px" : isMobile ? "20px" : "25px",
        marginTop: isSmallMobile ? "25px" : isMobile ? "30px" : "35px",
        marginBottom: isSmallMobile ? "25px" : isMobile ? "30px" : "35px",
      },
      subSectionTitle: {
        fontSize: isSmallMobile ? "16px" : "18px",
        marginBottom: isSmallMobile ? "12px" : "15px",
      },
      optionsRow: {
        justifyContent: isMobile ? "space-between" : "flex-start",
        gap: isSmallMobile ? "10px" : "15px",
        marginBottom: isSmallMobile ? "12px" : "15px",
      },
      optionButton: {
        flexBasis: isMobile ? "calc(50% - 10px)" : "auto",
        padding: isSmallMobile
          ? "10px 12px"
          : isMobile
          ? "12px 14px"
          : "12px 16px",
        minWidth: isSmallMobile ? "100px" : "120px",
        justifyContent: isMobile ? "center" : "flex-start",
      },
      finishSelector: {
        marginTop: isSmallMobile ? "15px" : "20px",
      },
      finishTitle: {
        fontSize: isSmallMobile ? "14px" : "15px",
        marginBottom: isSmallMobile ? "10px" : "12px",
      },
      finishOptions: {
        justifyContent: isMobile ? "space-around" : "flex-start",
      },
      checkboxGrid: {
        gridTemplateColumns: isSmallMobile
          ? "1fr"
          : isMobile
          ? "repeat(auto-fill, minmax(220px, 1fr))"
          : "repeat(auto-fill, minmax(250px, 1fr))",
        gap: isSmallMobile ? "10px" : "15px",
      },
      checkboxItem: {
        padding: isSmallMobile ? "12px" : "15px",
      },
      checkboxTitle: {
        fontSize: isSmallMobile ? "14px" : "15px",
      },
      checkboxDesc: {
        fontSize: isSmallMobile ? "12px" : "13px",
      },
      summaryPanel: {
        padding: isSmallMobile ? "15px" : "25px",
        marginBottom: isSmallMobile ? "20px" : "30px",
      },
      summaryTitle: {
        fontSize: isSmallMobile ? "16px" : "18px",
        marginBottom: isSmallMobile ? "15px" : "20px",
      },
      summaryGrid: {
        gridTemplateColumns: isSmallMobile
          ? "1fr"
          : isMobile
          ? "repeat(2, 1fr)"
          : "repeat(auto-fill, minmax(180px, 1fr))",
        gap: isSmallMobile ? "15px" : "20px",
      },
      summaryLabel: {
        fontSize: isSmallMobile ? "12px" : "13px",
      },
      summaryValue: {
        fontSize: isSmallMobile ? "14px" : "15px",
      },
      navigation: {
        flexDirection: isSmallMobile ? "column" : "row",
        gap: isSmallMobile ? "15px" : "0",
        marginBottom: isSmallMobile ? "15px" : "20px",
      },
      backButton: {
        width: isSmallMobile ? "100%" : "auto",
        justifyContent: isSmallMobile ? "center" : "flex-start",
        padding: isSmallMobile ? "12px 20px" : "14px 26px",
      },
      nextButton: {
        width: isSmallMobile ? "100%" : "auto",
        justifyContent: isSmallMobile ? "center" : "flex-start",
        padding: isSmallMobile ? "12px 20px" : "14px 26px",
      },
      previewNotice: {
        padding: isSmallMobile ? "12px 15px" : "15px 20px",
        flexDirection: isSmallMobile ? "column" : "row",
        alignItems: isSmallMobile ? "flex-start" : "center",
      },
      noticeIcon: {
        marginRight: isSmallMobile ? "0" : "15px",
        marginBottom: isSmallMobile ? "10px" : "0",
        fontSize: isSmallMobile ? "18px" : "22px",
      },
      noticeText: {
        fontSize: isSmallMobile ? "13px" : "14px",
      },
    };
  };

  const responsiveStyles = getResponsiveStyles();

  // Tabs configuration
  const tabs = [
    { id: "walls", label: "Walls", icon: "fa-th-large" },
    { id: "floor", label: "Floor", icon: "fa-border-all" },
    { id: "accessories", label: "Accessories", icon: "fa-grip-horizontal" },
  ];

  return (
    <div style={{ ...styles.container, ...responsiveStyles.container }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div style={{ ...styles.header, ...responsiveStyles.header }}>
          <h2 style={{ ...styles.title, ...responsiveStyles.title }}>
            <i className="fas fa-paint-roller" style={styles.titleIcon}></i>
            Interior Design & Finishes
          </h2>
          <p style={{ ...styles.subtitle, ...responsiveStyles.subtitle }}>
            Customize the interior appearance of your elevator
          </p>
        </div>

        {/* Tab Navigation */}
        <div
          style={{ ...styles.tabsContainer, ...responsiveStyles.tabsContainer }}
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              style={{
                ...styles.tabButton,
                ...responsiveStyles.tabButton,
                ...(activeTab === tab.id ? styles.activeTabButton : {}),
              }}
              onClick={() => setActiveTab(tab.id)}
            >
              <i className={`fas ${tab.icon}`} style={styles.tabIcon}></i>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        <div style={{ ...styles.mainContent, ...responsiveStyles.mainContent }}>
          {/* Ceiling Design Tab */}

          {/* Wall Design Tab */}
          {activeTab === "walls" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <h3
                style={{
                  ...styles.sectionTitle,
                  ...responsiveStyles.sectionTitle,
                }}
              >
                Wall Finishes
              </h3>
              <p
                style={{
                  ...styles.sectionDescription,
                  ...responsiveStyles.sectionDescription,
                }}
              >
                Select materials and finishes for your elevator walls
              </p>

              <div
                style={{
                  ...styles.optionsGrid,
                  ...responsiveStyles.optionsGrid,
                }}
              >
                {/* Stainless Steel Wall */}
                <div
                  style={{
                    ...styles.designCard,
                    ...(selections.wallFinish === "stainless"
                      ? styles.selectedCard
                      : {}),
                  }}
                  onClick={() =>
                    handleSelectionChange("wallFinish", "stainless")
                  }
                >
                  <div
                    style={{
                      ...styles.designVisual,
                      ...responsiveStyles.designVisual,
                    }}
                  >
                    <div style={styles.wallStainless}></div>
                  </div>
                  <div
                    style={{
                      ...styles.designInfo,
                      ...responsiveStyles.designInfo,
                    }}
                  >
                    <h4
                      style={{
                        ...styles.designTitle,
                        ...responsiveStyles.designTitle,
                      }}
                    >
                      Stainless Steel
                    </h4>
                    <p
                      style={{
                        ...styles.designDescription,
                        ...responsiveStyles.designDescription,
                      }}
                    >
                      Modern brushed or mirror finish stainless steel for a
                      sleek look
                    </p>
                  </div>
                  {selections.wallFinish === "stainless" && (
                    <div style={styles.selectedIndicator}>
                      <i className="fas fa-check-circle"></i>
                    </div>
                  )}
                </div>

                {/* Rose Gold Wall */}
                <div
                  style={{
                    ...styles.designCard,
                    ...(selections.wallFinish === "rose-gold"
                      ? styles.selectedCard
                      : {}),
                  }}
                  onClick={() =>
                    handleSelectionChange("wallFinish", "rose-gold")
                  }
                >
                  <div
                    style={{
                      ...styles.designVisual,
                      ...responsiveStyles.designVisual,
                    }}
                  >
                    <div style={styles.wallRoseGold}></div>
                  </div>
                  <div
                    style={{
                      ...styles.designInfo,
                      ...responsiveStyles.designInfo,
                    }}
                  >
                    <h4
                      style={{
                        ...styles.designTitle,
                        ...responsiveStyles.designTitle,
                      }}
                    >
                      Rose Gold
                    </h4>
                    <p
                      style={{
                        ...styles.designDescription,
                        ...responsiveStyles.designDescription,
                      }}
                    >
                      Elegant rose gold finish panels with luxurious metallic
                      appearance and warm tones
                    </p>
                  </div>
                  {selections.wallFinish === "rose-gold" && (
                    <div style={styles.selectedIndicator}>
                      <i className="fas fa-check-circle"></i>
                    </div>
                  )}
                </div>

                {/* Gold Wall */}
                <div
                  style={{
                    ...styles.designCard,
                    ...(selections.wallFinish === "gold"
                      ? styles.selectedCard
                      : {}),
                  }}
                  onClick={() => handleSelectionChange("wallFinish", "gold")}
                >
                  <div
                    style={{
                      ...styles.designVisual,
                      ...responsiveStyles.designVisual,
                    }}
                  >
                    <div style={styles.wallGold}></div>
                  </div>
                  <div
                    style={{
                      ...styles.designInfo,
                      ...responsiveStyles.designInfo,
                    }}
                  >
                    <h4
                      style={{
                        ...styles.designTitle,
                        ...responsiveStyles.designTitle,
                      }}
                    >
                      Gold
                    </h4>
                    <p
                      style={{
                        ...styles.designDescription,
                        ...responsiveStyles.designDescription,
                      }}
                    >
                      Luxurious gold finish panels with brilliant metallic shine
                      and premium appearance
                    </p>
                  </div>
                  {selections.wallFinish === "gold" && (
                    <div style={styles.selectedIndicator}>
                      <i className="fas fa-check-circle"></i>
                    </div>
                  )}
                </div>

                {/* Glass Wall */}
                <div
                  style={{
                    ...styles.designCard,
                    ...(selections.wallFinish === "glass"
                      ? styles.selectedCard
                      : {}),
                  }}
                  onClick={() => handleSelectionChange("wallFinish", "glass")}
                >
                  <div
                    style={{
                      ...styles.designVisual,
                      ...responsiveStyles.designVisual,
                    }}
                  >
                    <div style={styles.wallGlass}></div>
                  </div>
                  <div
                    style={{
                      ...styles.designInfo,
                      ...responsiveStyles.designInfo,
                    }}
                  >
                    <h4
                      style={{
                        ...styles.designTitle,
                        ...responsiveStyles.designTitle,
                      }}
                    >
                      Glass Panels
                    </h4>
                    <p
                      style={{
                        ...styles.designDescription,
                        ...responsiveStyles.designDescription,
                      }}
                    >
                      Sophisticated back-painted glass with a premium feel
                    </p>
                  </div>
                  {selections.wallFinish === "glass" && (
                    <div style={styles.selectedIndicator}>
                      <i className="fas fa-check-circle"></i>
                    </div>
                  )}
                </div>
              </div>

              {/* Wall Layout Options */}
              <div
                style={{ ...styles.subSection, ...responsiveStyles.subSection }}
              >
                <h4
                  style={{
                    ...styles.subSectionTitle,
                    ...responsiveStyles.subSectionTitle,
                  }}
                >
                  Panel Layout
                </h4>
                <p
                  style={{
                    ...styles.sectionDescription,
                    ...responsiveStyles.sectionDescription,
                  }}
                >
                  Choose how the wall panels are arranged
                </p>

                <div
                  style={{
                    ...styles.optionsRow,
                    ...responsiveStyles.optionsRow,
                  }}
                >
                  {/* Full Height Panels */}
                  <div
                    style={{
                      ...styles.optionButton,
                      ...responsiveStyles.optionButton,
                      ...(selections.wallLayout === "full-height"
                        ? styles.selectedOption
                        : {}),
                    }}
                    onClick={() =>
                      handleSelectionChange("wallLayout", "full-height")
                    }
                  >
                    <div style={styles.optionIcon}>
                      <i className="fas fa-arrows-alt-v"></i>
                    </div>
                    <span>Full Height</span>
                    {selections.wallLayout === "full-height" && (
                      <i
                        className="fas fa-check-circle"
                        style={styles.optionCheck}
                      ></i>
                    )}
                  </div>

                  {/* Segmented Panels */}
                  <div
                    style={{
                      ...styles.optionButton,
                      ...responsiveStyles.optionButton,
                      ...(selections.wallLayout === "segmented"
                        ? styles.selectedOption
                        : {}),
                    }}
                    onClick={() =>
                      handleSelectionChange("wallLayout", "segmented")
                    }
                  >
                    <div style={styles.optionIcon}>
                      <i className="fas fa-table"></i>
                    </div>
                    <span>Segmented</span>
                    {selections.wallLayout === "segmented" && (
                      <i
                        className="fas fa-check-circle"
                        style={styles.optionCheck}
                      ></i>
                    )}
                  </div>

                  {/* Horizontal Split */}
                  <div
                    style={{
                      ...styles.optionButton,
                      ...responsiveStyles.optionButton,
                      ...(selections.wallLayout === "horizontal-split"
                        ? styles.selectedOption
                        : {}),
                    }}
                    onClick={() =>
                      handleSelectionChange("wallLayout", "horizontal-split")
                    }
                  >
                    <div style={styles.optionIcon}>
                      <i className="fas fa-grip-lines-vertical"></i>
                    </div>
                    <span>Horizontal Split</span>
                    {selections.wallLayout === "horizontal-split" && (
                      <i
                        className="fas fa-check-circle"
                        style={styles.optionCheck}
                      ></i>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Floor Design Tab */}
          {activeTab === "floor" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <h3
                style={{
                  ...styles.sectionTitle,
                  ...responsiveStyles.sectionTitle,
                }}
              >
                Floor Designs
              </h3>
              <p
                style={{
                  ...styles.sectionDescription,
                  ...responsiveStyles.sectionDescription,
                }}
              >
                Choose flooring materials that combine style and durability
              </p>

              <div
                style={{
                  ...styles.optionsGrid,
                  ...responsiveStyles.optionsGrid,
                }}
              >
                {/* Anti-skid PVC Floor */}
                <div
                  style={{
                    ...styles.designCard,
                    ...(selections.floorType === "anti-skid-pvc"
                      ? styles.selectedCard
                      : {}),
                  }}
                  onClick={() =>
                    handleSelectionChange("floorType", "anti-skid-pvc")
                  }
                >
                  <div
                    style={{
                      ...styles.designVisual,
                      ...responsiveStyles.designVisual,
                    }}
                  >
                    <div style={styles.floorAntiSkidPvc}></div>
                  </div>
                  <div
                    style={{
                      ...styles.designInfo,
                      ...responsiveStyles.designInfo,
                    }}
                  >
                    <h4
                      style={{
                        ...styles.designTitle,
                        ...responsiveStyles.designTitle,
                      }}
                    >
                      Anti-skid PVC
                    </h4>
                    <p
                      style={{
                        ...styles.designDescription,
                        ...responsiveStyles.designDescription,
                      }}
                    >
                      Non-slip PVC flooring with textured surface for enhanced
                      safety and grip
                    </p>
                  </div>
                  {selections.floorType === "anti-skid-pvc" && (
                    <div style={styles.selectedIndicator}>
                      <i className="fas fa-check-circle"></i>
                    </div>
                  )}
                </div>

                {/* Checkered Plates Floor */}
                <div
                  style={{
                    ...styles.designCard,
                    ...(selections.floorType === "checkered-plates"
                      ? styles.selectedCard
                      : {}),
                  }}
                  onClick={() =>
                    handleSelectionChange("floorType", "checkered-plates")
                  }
                >
                  <div
                    style={{
                      ...styles.designVisual,
                      ...responsiveStyles.designVisual,
                    }}
                  >
                    <div style={styles.floorCheckeredPlates}></div>
                  </div>
                  <div
                    style={{
                      ...styles.designInfo,
                      ...responsiveStyles.designInfo,
                    }}
                  >
                    <h4
                      style={{
                        ...styles.designTitle,
                        ...responsiveStyles.designTitle,
                      }}
                    >
                      Checkered Plates
                    </h4>
                    <p
                      style={{
                        ...styles.designDescription,
                        ...responsiveStyles.designDescription,
                      }}
                    >
                      Industrial-grade metal checkered plates for maximum
                      durability and slip resistance
                    </p>
                  </div>
                  {selections.floorType === "checkered-plates" && (
                    <div style={styles.selectedIndicator}>
                      <i className="fas fa-check-circle"></i>
                    </div>
                  )}
                </div>

                {/* Stone Floor (Client Scope) */}
                <div
                  style={{
                    ...styles.designCard,
                    ...(selections.floorType === "stone-client-scope"
                      ? styles.selectedCard
                      : {}),
                  }}
                  onClick={() =>
                    handleSelectionChange("floorType", "stone-client-scope")
                  }
                >
                  <div
                    style={{
                      ...styles.designVisual,
                      ...responsiveStyles.designVisual,
                    }}
                  >
                    <div style={styles.floorStoneClientScope}></div>
                  </div>
                  <div
                    style={{
                      ...styles.designInfo,
                      ...responsiveStyles.designInfo,
                    }}
                  >
                    <h4
                      style={{
                        ...styles.designTitle,
                        ...responsiveStyles.designTitle,
                      }}
                    >
                      Stone (Client Scope)
                    </h4>
                    <p
                      style={{
                        ...styles.designDescription,
                        ...responsiveStyles.designDescription,
                      }}
                    >
                      Natural stone flooring provided and installed by client
                      according to their specifications
                    </p>
                  </div>
                  {selections.floorType === "stone-client-scope" && (
                    <div style={styles.selectedIndicator}>
                      <i className="fas fa-check-circle"></i>
                    </div>
                  )}
                </div>
              </div>

              {/* Floor Pattern Options */}
              <div
                style={{ ...styles.subSection, ...responsiveStyles.subSection }}
              >
                <h4
                  style={{
                    ...styles.subSectionTitle,
                    ...responsiveStyles.subSectionTitle,
                  }}
                >
                  Floor Pattern
                </h4>
                <p
                  style={{
                    ...styles.sectionDescription,
                    ...responsiveStyles.sectionDescription,
                  }}
                >
                  Select your preferred pattern style
                </p>

                <div
                  style={{
                    ...styles.optionsRow,
                    ...responsiveStyles.optionsRow,
                  }}
                >
                  {/* Solid Pattern */}
                  <div
                    style={{
                      ...styles.optionButton,
                      ...responsiveStyles.optionButton,
                      ...(selections.floorPattern === "solid"
                        ? styles.selectedOption
                        : {}),
                    }}
                    onClick={() =>
                      handleSelectionChange("floorPattern", "solid")
                    }
                  >
                    <div style={styles.optionIcon}>
                      <i className="fas fa-square"></i>
                    </div>
                    <span>Solid</span>
                    {selections.floorPattern === "solid" && (
                      <i
                        className="fas fa-check-circle"
                        style={styles.optionCheck}
                      ></i>
                    )}
                  </div>

                  {/* Grid Pattern */}
                  <div
                    style={{
                      ...styles.optionButton,
                      ...responsiveStyles.optionButton,
                      ...(selections.floorPattern === "grid"
                        ? styles.selectedOption
                        : {}),
                    }}
                    onClick={() =>
                      handleSelectionChange("floorPattern", "grid")
                    }
                  >
                    <div style={styles.optionIcon}>
                      <i className="fas fa-th-large"></i>
                    </div>
                    <span>Grid</span>
                    {selections.floorPattern === "grid" && (
                      <i
                        className="fas fa-check-circle"
                        style={styles.optionCheck}
                      ></i>
                    )}
                  </div>

                  {/* Diagonal Pattern */}
                  <div
                    style={{
                      ...styles.optionButton,
                      ...responsiveStyles.optionButton,
                      ...(selections.floorPattern === "diagonal"
                        ? styles.selectedOption
                        : {}),
                    }}
                    onClick={() =>
                      handleSelectionChange("floorPattern", "diagonal")
                    }
                  >
                    <div style={styles.optionIcon}>
                      <i className="fas fa-th"></i>
                    </div>
                    <span>Diagonal</span>
                    {selections.floorPattern === "diagonal" && (
                      <i
                        className="fas fa-check-circle"
                        style={styles.optionCheck}
                      ></i>
                    )}
                  </div>

                  {/* Border Pattern */}
                  <div
                    style={{
                      ...styles.optionButton,
                      ...responsiveStyles.optionButton,
                      ...(selections.floorPattern === "border"
                        ? styles.selectedOption
                        : {}),
                    }}
                    onClick={() =>
                      handleSelectionChange("floorPattern", "border")
                    }
                  >
                    <div style={styles.optionIcon}>
                      <i className="far fa-square"></i>
                    </div>
                    <span>Border</span>
                    {selections.floorPattern === "border" && (
                      <i
                        className="fas fa-check-circle"
                        style={styles.optionCheck}
                      ></i>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Accessories Tab */}
          {activeTab === "accessories" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <h3
                style={{
                  ...styles.sectionTitle,
                  ...responsiveStyles.sectionTitle,
                }}
              >
                Handrails & Accessories
              </h3>
              <p
                style={{
                  ...styles.sectionDescription,
                  ...responsiveStyles.sectionDescription,
                }}
              >
                Select finishing touches and functional accessories
              </p>

              {/* Handrail Options */}
              <div
                style={{ ...styles.subSection, ...responsiveStyles.subSection }}
              >
                <h4
                  style={{
                    ...styles.subSectionTitle,
                    ...responsiveStyles.subSectionTitle,
                  }}
                >
                  Handrails
                </h4>
                <p
                  style={{
                    ...styles.sectionDescription,
                    ...responsiveStyles.sectionDescription,
                  }}
                >
                  Choose your preferred handrail style
                </p>

                <div
                  style={{
                    ...styles.optionsRow,
                    ...responsiveStyles.optionsRow,
                  }}
                >
                  {/* Round Handrail */}
                  <div
                    style={{
                      ...styles.optionButton,
                      ...responsiveStyles.optionButton,
                      ...(selections.handrailType === "round"
                        ? styles.selectedOption
                        : {}),
                    }}
                    onClick={() =>
                      handleSelectionChange("handrailType", "round")
                    }
                  >
                    <div style={styles.optionIcon}>
                      <i className="fas fa-minus"></i>
                    </div>
                    <span>Round</span>
                    {selections.handrailType === "round" && (
                      <i
                        className="fas fa-check-circle"
                        style={styles.optionCheck}
                      ></i>
                    )}
                  </div>

                  {/* Flat Handrail */}
                  <div
                    style={{
                      ...styles.optionButton,
                      ...responsiveStyles.optionButton,
                      ...(selections.handrailType === "flat"
                        ? styles.selectedOption
                        : {}),
                    }}
                    onClick={() =>
                      handleSelectionChange("handrailType", "flat")
                    }
                  >
                    <div style={styles.optionIcon}>
                      <i className="fas fa-grip-lines"></i>
                    </div>
                    <span>Flat</span>
                    {selections.handrailType === "flat" && (
                      <i
                        className="fas fa-check-circle"
                        style={styles.optionCheck}
                      ></i>
                    )}
                  </div>

                  {/* Designer Handrail */}
                  <div
                    style={{
                      ...styles.optionButton,
                      ...responsiveStyles.optionButton,
                      ...(selections.handrailType === "designer"
                        ? styles.selectedOption
                        : {}),
                    }}
                    onClick={() =>
                      handleSelectionChange("handrailType", "designer")
                    }
                  >
                    <div style={styles.optionIcon}>
                      <i className="fas fa-vector-square"></i>
                    </div>
                    <span>Designer</span>
                    {selections.handrailType === "designer" && (
                      <i
                        className="fas fa-check-circle"
                        style={styles.optionCheck}
                      ></i>
                    )}
                  </div>

                  {/* No Handrail */}
                  <div
                    style={{
                      ...styles.optionButton,
                      ...responsiveStyles.optionButton,
                      ...(selections.handrailType === "none"
                        ? styles.selectedOption
                        : {}),
                    }}
                    onClick={() =>
                      handleSelectionChange("handrailType", "none")
                    }
                  >
                    <div style={styles.optionIcon}>
                      <i className="fas fa-times"></i>
                    </div>
                    <span>None</span>
                    {selections.handrailType === "none" && (
                      <i
                        className="fas fa-check-circle"
                        style={styles.optionCheck}
                      ></i>
                    )}
                  </div>
                </div>

                {selections.handrailType !== "none" && (
                  <div
                    style={{
                      ...styles.finishSelector,
                      ...responsiveStyles.finishSelector,
                    }}
                  >
                    <h5
                      style={{
                        ...styles.finishTitle,
                        ...responsiveStyles.finishTitle,
                      }}
                    >
                      Handrail Finish
                    </h5>
                    <div
                      style={{
                        ...styles.finishOptions,
                        ...responsiveStyles.finishOptions,
                      }}
                    >
                      <div
                        style={{
                          ...styles.finishSwatch,
                          ...styles.stainlessFinish,
                          ...(selections.handrailFinish === "stainless"
                            ? styles.selectedFinish
                            : {}),
                        }}
                        onClick={() =>
                          handleSelectionChange("handrailFinish", "stainless")
                        }
                        title="Stainless Steel"
                      >
                        {selections.handrailFinish === "stainless" && (
                          <div style={styles.finishCheck}>
                            <i className="fas fa-check"></i>
                          </div>
                        )}
                      </div>
                      <div
                        style={{
                          ...styles.finishSwatch,
                          ...styles.goldFinish,
                          ...(selections.handrailFinish === "gold"
                            ? styles.selectedFinish
                            : {}),
                        }}
                        onClick={() =>
                          handleSelectionChange("handrailFinish", "gold")
                        }
                        title="Gold"
                      >
                        {selections.handrailFinish === "gold" && (
                          <div style={styles.finishCheck}>
                            <i className="fas fa-check"></i>
                          </div>
                        )}
                      </div>
                      <div
                        style={{
                          ...styles.finishSwatch,
                          ...styles.woodFinish,
                          ...(selections.handrailFinish === "wood"
                            ? styles.selectedFinish
                            : {}),
                        }}
                        onClick={() =>
                          handleSelectionChange("handrailFinish", "wood")
                        }
                        title="Wood"
                      >
                        {selections.handrailFinish === "wood" && (
                          <div style={styles.finishCheck}>
                            <i className="fas fa-check"></i>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* COP/LOP Options */}
              <div
                style={{ ...styles.subSection, ...responsiveStyles.subSection }}
              >
                <h4
                  style={{
                    ...styles.subSectionTitle,
                    ...responsiveStyles.subSectionTitle,
                  }}
                >
                  COP/LOP
                </h4>
                <p
                  style={{
                    ...styles.sectionDescription,
                    ...responsiveStyles.sectionDescription,
                  }}
                >
                  Select the type for your elevator controls (Car Operating
                  Panel / Landing Operating Panel)
                </p>

                <div
                  style={{
                    ...styles.optionsRow,
                    ...responsiveStyles.optionsRow,
                  }}
                >
                  {/* Touch Panel COP/LOP */}
                  <div
                    style={{
                      ...styles.optionButton,
                      ...responsiveStyles.optionButton,
                      ...(selections.controlPanel === "touch-panel"
                        ? styles.selectedOption
                        : {}),
                    }}
                    onClick={() =>
                      handleSelectionChange("controlPanel", "touch-panel")
                    }
                  >
                    <div style={styles.optionIcon}>
                      <i className="fas fa-tablet-alt"></i>
                    </div>
                    <span>Touch Panel</span>
                    {selections.controlPanel === "touch-panel" && (
                      <i
                        className="fas fa-check-circle"
                        style={styles.optionCheck}
                      ></i>
                    )}
                  </div>

                  {/* Push Buttons COP/LOP */}
                  <div
                    style={{
                      ...styles.optionButton,
                      ...responsiveStyles.optionButton,
                      ...(selections.controlPanel === "push-buttons"
                        ? styles.selectedOption
                        : {}),
                    }}
                    onClick={() =>
                      handleSelectionChange("controlPanel", "push-buttons")
                    }
                  >
                    <div style={styles.optionIcon}>
                      <i className="fas fa-th-list"></i>
                    </div>
                    <span>Push Buttons</span>
                    {selections.controlPanel === "push-buttons" && (
                      <i
                        className="fas fa-check-circle"
                        style={styles.optionCheck}
                      ></i>
                    )}
                  </div>
                </div>

                <div
                  style={{
                    ...styles.finishSelector,
                    ...responsiveStyles.finishSelector,
                  }}
                >
                  <h5
                    style={{
                      ...styles.finishTitle,
                      ...responsiveStyles.finishTitle,
                    }}
                  >
                    COP/LOP Finish
                  </h5>
                  <div
                    style={{
                      ...styles.finishOptions,
                      ...responsiveStyles.finishOptions,
                    }}
                  >
                    <div
                      style={{
                        ...styles.finishSwatch,
                        ...styles.stainlessFinish,
                        ...(selections.controlFinish === "stainless"
                          ? styles.selectedFinish
                          : {}),
                      }}
                      onClick={() =>
                        handleSelectionChange("controlFinish", "stainless")
                      }
                      title="Stainless Steel"
                    >
                      {selections.controlFinish === "stainless" && (
                        <div style={styles.finishCheck}>
                          <i className="fas fa-check"></i>
                        </div>
                      )}
                    </div>
                    <div
                      style={{
                        ...styles.finishSwatch,
                        ...styles.goldFinish,
                        ...(selections.controlFinish === "gold"
                          ? styles.selectedFinish
                          : {}),
                      }}
                      onClick={() =>
                        handleSelectionChange("controlFinish", "gold")
                      }
                      title="Gold"
                    >
                      {selections.controlFinish === "gold" && (
                        <div style={styles.finishCheck}>
                          <i className="fas fa-check"></i>
                        </div>
                      )}
                    </div>
                    <div
                      style={{
                        ...styles.finishSwatch,
                        ...styles.blackFinish,
                        ...(selections.controlFinish === "black"
                          ? styles.selectedFinish
                          : {}),
                      }}
                      onClick={() =>
                        handleSelectionChange("controlFinish", "black")
                      }
                      title="Black"
                    >
                      {selections.controlFinish === "black" && (
                        <div style={styles.finishCheck}>
                          <i className="fas fa-check"></i>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Accessories */}
              <div
                style={{ ...styles.subSection, ...responsiveStyles.subSection }}
              >
                <h4
                  style={{
                    ...styles.subSectionTitle,
                    ...responsiveStyles.subSectionTitle,
                  }}
                >
                  Additional Features
                </h4>
                <p
                  style={{
                    ...styles.sectionDescription,
                    ...responsiveStyles.sectionDescription,
                  }}
                >
                  Select optional accessories to enhance your elevator
                </p>

                <div
                  style={{
                    ...styles.checkboxGrid,
                    ...responsiveStyles.checkboxGrid,
                  }}
                >
                  {/* Mirror */}
                  <div
                    style={{
                      ...styles.checkboxItem,
                      ...responsiveStyles.checkboxItem,
                      ...(selections.mirror ? styles.checkboxSelected : {}),
                    }}
                    onClick={() =>
                      handleSelectionChange("mirror", !selections.mirror)
                    }
                  >
                    <div style={styles.checkbox}>
                      {selections.mirror && <i className="fas fa-check"></i>}
                    </div>
                    <div style={styles.checkboxLabel}>
                      <span
                        style={{
                          ...styles.checkboxTitle,
                          ...responsiveStyles.checkboxTitle,
                        }}
                      >
                        Mirror Panel
                      </span>
                      <span
                        style={{
                          ...styles.checkboxDesc,
                          ...responsiveStyles.checkboxDesc,
                        }}
                      >
                        Half or full-height mirror on rear or side wall
                      </span>
                    </div>
                  </div>

                  {/* Display Screen */}
                  <div
                    style={{
                      ...styles.checkboxItem,
                      ...responsiveStyles.checkboxItem,
                      ...(selections.display ? styles.checkboxSelected : {}),
                    }}
                    onClick={() =>
                      handleSelectionChange("display", !selections.display)
                    }
                  >
                    <div style={styles.checkbox}>
                      {selections.display && <i className="fas fa-check"></i>}
                    </div>
                    <div style={styles.checkboxLabel}>
                      <span
                        style={{
                          ...styles.checkboxTitle,
                          ...responsiveStyles.checkboxTitle,
                        }}
                      >
                        Digital Display
                      </span>
                      <span
                        style={{
                          ...styles.checkboxDesc,
                          ...responsiveStyles.checkboxDesc,
                        }}
                      >
                        Multimedia screen for information and entertainment
                      </span>
                    </div>
                  </div>

                  {/* Ventilation Fan */}
                  <div
                    style={{
                      ...styles.checkboxItem,
                      ...responsiveStyles.checkboxItem,
                      ...(selections.ventilation
                        ? styles.checkboxSelected
                        : {}),
                    }}
                    onClick={() =>
                      handleSelectionChange(
                        "ventilation",
                        !selections.ventilation
                      )
                    }
                  >
                    <div style={styles.checkbox}>
                      {selections.ventilation && (
                        <i className="fas fa-check"></i>
                      )}
                    </div>
                    <div style={styles.checkboxLabel}>
                      <span
                        style={{
                          ...styles.checkboxTitle,
                          ...responsiveStyles.checkboxTitle,
                        }}
                      >
                        Ventilation System
                      </span>
                      <span
                        style={{
                          ...styles.checkboxDesc,
                          ...responsiveStyles.checkboxDesc,
                        }}
                      >
                        Enhanced air circulation and filtration
                      </span>
                    </div>
                  </div>

                  {/* Kick Plates */}
                  <div
                    style={{
                      ...styles.checkboxItem,
                      ...responsiveStyles.checkboxItem,
                      ...(selections.kickPlates ? styles.checkboxSelected : {}),
                    }}
                    onClick={() =>
                      handleSelectionChange(
                        "kickPlates",
                        !selections.kickPlates
                      )
                    }
                  >
                    <div style={styles.checkbox}>
                      {selections.kickPlates && (
                        <i className="fas fa-check"></i>
                      )}
                    </div>
                    <div style={styles.checkboxLabel}>
                      <span
                        style={{
                          ...styles.checkboxTitle,
                          ...responsiveStyles.checkboxTitle,
                        }}
                      >
                        Kick Plates
                      </span>
                      <span
                        style={{
                          ...styles.checkboxDesc,
                          ...responsiveStyles.checkboxDesc,
                        }}
                      >
                        Protective plates for the lower section of walls
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Selection Summary */}
        <div
          style={{ ...styles.summaryPanel, ...responsiveStyles.summaryPanel }}
        >
          <h4
            style={{ ...styles.summaryTitle, ...responsiveStyles.summaryTitle }}
          >
            Your Design Selections
          </h4>

          <div
            style={{ ...styles.summaryGrid, ...responsiveStyles.summaryGrid }}
          >
            <div style={styles.summaryItem}>
              <div
                style={{
                  ...styles.summaryLabel,
                  ...responsiveStyles.summaryLabel,
                }}
              >
                Lighting
              </div>
              <div
                style={{
                  ...styles.summaryValue,
                  ...responsiveStyles.summaryValue,
                }}
              >
                {selections.lighting === "led-spots"
                  ? "LED Spots"
                  : selections.lighting === "linear"
                  ? "Linear"
                  : selections.lighting === "ambient"
                  ? "Ambient"
                  : "Dynamic"}
              </div>
            </div>

            {/* Wall Section */}
            <div style={styles.summaryItem}>
              <div
                style={{
                  ...styles.summaryLabel,
                  ...responsiveStyles.summaryLabel,
                }}
              >
                Wall Finish
              </div>
              <div
                style={{
                  ...styles.summaryValue,
                  ...responsiveStyles.summaryValue,
                }}
              >
                {selections.wallFinish === "stainless"
                  ? "Stainless Steel"
                  : selections.wallFinish === "rose-gold"
                  ? "Rose Gold"
                  : selections.wallFinish === "gold"
                  ? "Gold"
                  : "Glass Panels"}
              </div>
            </div>

            {selections.wallLayout && (
              <div style={styles.summaryItem}>
                <div
                  style={{
                    ...styles.summaryLabel,
                    ...responsiveStyles.summaryLabel,
                  }}
                >
                  Wall Layout
                </div>
                <div
                  style={{
                    ...styles.summaryValue,
                    ...responsiveStyles.summaryValue,
                  }}
                >
                  {selections.wallLayout === "full-height"
                    ? "Full Height Panels"
                    : selections.wallLayout === "segmented"
                    ? "Segmented Panels"
                    : "Horizontal Split"}
                </div>
              </div>
            )}

            {/* Floor Section */}
            <div style={styles.summaryItem}>
              <div
                style={{
                  ...styles.summaryLabel,
                  ...responsiveStyles.summaryLabel,
                }}
              >
                Floor Material
              </div>
              <div
                style={{
                  ...styles.summaryValue,
                  ...responsiveStyles.summaryValue,
                }}
              >
                {selections.floorType === "anti-skid-pvc"
                  ? "Anti-skid PVC"
                  : selections.floorType === "checkered-plates"
                  ? "Checkered Plates"
                  : "Stone (Client Scope)"}
              </div>
            </div>

            {selections.floorPattern && (
              <div style={styles.summaryItem}>
                <div
                  style={{
                    ...styles.summaryLabel,
                    ...responsiveStyles.summaryLabel,
                  }}
                >
                  Floor Pattern
                </div>
                <div
                  style={{
                    ...styles.summaryValue,
                    ...responsiveStyles.summaryValue,
                  }}
                >
                  {selections.floorPattern === "solid"
                    ? "Solid"
                    : selections.floorPattern === "grid"
                    ? "Grid"
                    : selections.floorPattern === "diagonal"
                    ? "Diagonal"
                    : "Border"}
                </div>
              </div>
            )}

            {/* Handrail Section */}
            <div style={styles.summaryItem}>
              <div
                style={{
                  ...styles.summaryLabel,
                  ...responsiveStyles.summaryLabel,
                }}
              >
                Handrail
              </div>
              <div
                style={{
                  ...styles.summaryValue,
                  ...responsiveStyles.summaryValue,
                }}
              >
                {selections.handrailType === "none"
                  ? "None"
                  : `${
                      selections.handrailType === "round"
                        ? "Round"
                        : selections.handrailType === "flat"
                        ? "Flat"
                        : "Designer"
                    }`}
              </div>
            </div>

            {selections.handrailType !== "none" &&
              selections.handrailFinish && (
                <div style={styles.summaryItem}>
                  <div
                    style={{
                      ...styles.summaryLabel,
                      ...responsiveStyles.summaryLabel,
                    }}
                  >
                    Handrail Finish
                  </div>
                  <div
                    style={{
                      ...styles.summaryValue,
                      ...responsiveStyles.summaryValue,
                    }}
                  >
                    {selections.handrailFinish === "stainless"
                      ? "Stainless Steel"
                      : selections.handrailFinish === "gold"
                      ? "Gold"
                      : "Wood"}
                  </div>
                </div>
              )}

            {/* Control Panel */}
            <div style={styles.summaryItem}>
              <div
                style={{
                  ...styles.summaryLabel,
                  ...responsiveStyles.summaryLabel,
                }}
              >
                COP/LOP
              </div>
              <div
                style={{
                  ...styles.summaryValue,
                  ...responsiveStyles.summaryValue,
                }}
              >
                {selections.controlPanel === "touch-panel"
                  ? "Touch Panel"
                  : "Push Buttons"}
              </div>
            </div>

            {selections.controlFinish && (
              <div style={styles.summaryItem}>
                <div
                  style={{
                    ...styles.summaryLabel,
                    ...responsiveStyles.summaryLabel,
                  }}
                >
                  COP/LOP Finish
                </div>
                <div
                  style={{
                    ...styles.summaryValue,
                    ...responsiveStyles.summaryValue,
                  }}
                >
                  {selections.controlFinish === "stainless"
                    ? "Stainless Steel"
                    : selections.controlFinish === "gold"
                    ? "Gold"
                    : "Black"}
                </div>
              </div>
            )}

            {/* Additional Features */}
            <div style={styles.summaryItem}>
              <div
                style={{
                  ...styles.summaryLabel,
                  ...responsiveStyles.summaryLabel,
                }}
              >
                Additional Features
              </div>
              <div
                style={{
                  ...styles.summaryValue,
                  ...responsiveStyles.summaryValue,
                }}
              >
                {!selections.mirror &&
                !selections.display &&
                !selections.ventilation &&
                !selections.kickPlates ? (
                  "None"
                ) : (
                  <ul style={styles.featuresList}>
                    {selections.mirror && <li>Mirror Panel</li>}
                    {selections.display && <li>Digital Display</li>}
                    {selections.ventilation && <li>Ventilation System</li>}
                    {selections.kickPlates && <li>Kick Plates</li>}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div style={{ ...styles.navigation, ...responsiveStyles.navigation }}>
          <motion.button
            whileHover={
              !isSmallMobile ? { scale: 1.03, backgroundColor: "#f5f5f7" } : {}
            }
            whileTap={{ scale: 0.98 }}
            onClick={onBack}
            style={{ ...styles.backButton, ...responsiveStyles.backButton }}
          >
            <i className="fas fa-arrow-left"></i> Back
          </motion.button>

          <motion.button
            whileHover={
              !isSmallMobile
                ? { scale: 1.03, boxShadow: "0 8px 20px rgba(0,122,255,0.3)" }
                : {}
            }
            whileTap={{ scale: 0.98 }}
            onClick={onContinue}
            style={{ ...styles.nextButton, ...responsiveStyles.nextButton }}
          >
            Continue <i className="fas fa-arrow-right"></i>
          </motion.button>
        </div>

        {/* Preview Notice */}
        <div
          style={{ ...styles.previewNotice, ...responsiveStyles.previewNotice }}
        >
          <div style={{ ...styles.noticeIcon, ...responsiveStyles.noticeIcon }}>
            <i className="fas fa-info-circle"></i>
          </div>
          <p style={{ ...styles.noticeText, ...responsiveStyles.noticeText }}>
            After finalizing your configuration, you'll receive a detailed
            summery of your elevator interior design.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

// Add these styles to your component

const styles = {
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "20px",
    fontFamily: "'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif",
    color: "#333",
  },
  header: {
    marginBottom: "30px",
  },
  title: {
    fontSize: "28px",
    fontWeight: "700",
    margin: "0 0 6px",
    color: "#1d1d1f",
    display: "flex",
    alignItems: "center",
  },
  titleIcon: {
    marginRight: "10px",
    color: "#007aff",
  },
  subtitle: {
    fontSize: "16px",
    color: "#4d4d4d",
    margin: 0,
    lineHeight: 1.5,
  },

  // Tab Navigation
  tabsContainer: {
    display: "flex",
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    backgroundColor: "#f5f5f7",
    marginBottom: "25px",
  },
  tabButton: {
    flex: 1,
    padding: "16px 10px",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
    fontSize: "14px",
    fontWeight: "500",
    color: "#4d4d4d",
    transition: "all 0.2s ease",
  },
  activeTabButton: {
    backgroundColor: "#ffffff",
    color: "#007aff",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  },
  tabIcon: {
    fontSize: "18px",
    transition: "transform 0.2s ease",
  },

  // Main content area
  mainContent: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    padding: "30px",
    boxShadow: "0 5px 20px rgba(0,0,0,0.06)",
    marginBottom: "30px",
  },
  sectionTitle: {
    fontSize: "22px",
    fontWeight: "600",
    margin: "0 0 8px",
    color: "#1d1d1f",
  },
  sectionDescription: {
    fontSize: "15px",
    color: "#666",
    marginBottom: "25px",
    lineHeight: 1.5,
  },

  // Design cards grid
  optionsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
    gap: "25px",
    marginBottom: "30px",
  },
  designCard: {
    display: "flex",
    flexDirection: "column",
    border: "1px solid #e0e0e0",
    borderRadius: "14px",
    overflow: "hidden",
    transition: "all 0.3s ease",
    backgroundColor: "#fff",
    boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
    cursor: "pointer",
    position: "relative",
    height: "100%",
    "&:hover": {
      transform: "translateY(-3px)",
      boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
      borderColor: "#b0d5ff",
    },
  },
  selectedCard: {
    borderColor: "#007aff",
    boxShadow: "0 5px 20px rgba(0,122,255,0.15)",
    backgroundColor: "#f0f7ff",
  },
  designVisual: {
    height: "160px",
    overflow: "hidden",
    position: "relative",
  },
  designInfo: {
    padding: "18px",
    flex: 1,
  },
  designTitle: {
    fontSize: "16px",
    fontWeight: "600",
    marginTop: "0",
    marginBottom: "10px",
    color: "#333",
  },
  designDescription: {
    fontSize: "14px",
    color: "#666",
    lineHeight: "1.5",
    margin: "0",
  },
  selectedIndicator: {
    position: "absolute",
    top: "15px",
    right: "15px",
    fontSize: "22px",
    color: "#007aff",
    backgroundColor: "rgba(255,255,255,0.9)",
    borderRadius: "50%",
    padding: "2px",
    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
  },

  // Ceiling visuals
  ceilingClassic: {
    width: "100%",
    height: "100%",
    background: "linear-gradient(to bottom, #f0f0f0, #e8e8e8)",
    borderBottom: "1px solid #ddd",
    display: "flex",
    justifyContent: "center",
    position: "relative",
    "&::after": {
      content: '""',
      position: "absolute",
      width: "60%",
      height: "5px",
      backgroundColor: "#ddd",
      bottom: "50%",
      borderRadius: "3px",
    },
  },
  ceilingContemporary: {
    width: "100%",
    height: "100%",
    background: "linear-gradient(to bottom, #f5f5f5, #efefef)",
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      top: "30%",
      left: "10%",
      right: "10%",
      height: "2px",
      background: "linear-gradient(to right, transparent, #bbb, transparent)",
    },
    "&::after": {
      content: '""',
      position: "absolute",
      bottom: "30%",
      left: "10%",
      right: "10%",
      height: "2px",
      background: "linear-gradient(to right, transparent, #bbb, transparent)",
    },
  },
  ceilingSuspended: {
    width: "100%",
    height: "100%",
    background: "#f9f9f9",
    position: "relative",
    overflow: "hidden",
    "&::before": {
      content: '""',
      position: "absolute",
      top: "20px",
      left: "20px",
      right: "20px",
      bottom: "20px",
      backgroundColor: "#e8e8e8",
      boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
      borderRadius: "5px",
    },
  },
  ceilingBacklit: {
    width: "100%",
    height: "100%",
    background: "linear-gradient(to bottom, #f8f8f8, #f0f0f0)",
    position: "relative",
    overflow: "hidden",
    "&::before": {
      content: '""',
      position: "absolute",
      top: "10px",
      left: "10px",
      right: "10px",
      bottom: "10px",
      backgroundColor: "rgba(255,255,255,0.9)",
      boxShadow:
        "0 0 20px rgba(255,255,255,0.8), 0 0 30px rgba(100,180,255,0.4)",
      borderRadius: "5px",
    },
  },

  // Wall visuals
  wallStainless: {
    width: "100%",
    height: "100%",
    background: "linear-gradient(135deg, #b8b8b8, #e8e8e8, #d4d4d4)",
  },
  wallRoseGold: {
    width: "100%",
    height: "100%",
    background: "linear-gradient(135deg, #E8B4B8, #F4C2C2, #E8B4B8)",
    position: "relative",
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background:
        "linear-gradient(to right, rgba(255,255,255,0.2), rgba(255,255,255,0.1), rgba(255,255,255,0.2))",
    },
  },
  wallGold: {
    width: "100%",
    height: "100%",
    background: "linear-gradient(135deg, #d4af37, #f9d776, #d4af37)",
    position: "relative",
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background:
        "linear-gradient(to right, rgba(255,255,255,0.3), rgba(255,255,255,0.1), rgba(255,255,255,0.3))",
    },
  },
  wallGlass: {
    width: "100%",
    height: "100%",
    background:
      "linear-gradient(135deg, rgba(220,240,255,0.7), rgba(240,250,255,0.9), rgba(220,240,255,0.7))",
    position: "relative",
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background:
        "linear-gradient(to right, rgba(255,255,255,0.1), rgba(255,255,255,0.3), rgba(255,255,255,0.1))",
    },
  },

  // Floor visuals
  floorAntiSkidPvc: {
    width: "100%",
    height: "100%",
    background: "#4A5568",
    position: "relative",
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: `
      repeating-linear-gradient(
        45deg,
        transparent,
        transparent 2px,
        rgba(255,255,255,0.1) 2px,
        rgba(255,255,255,0.1) 4px
      ),
      repeating-linear-gradient(
        -45deg,
        transparent,
        transparent 2px,
        rgba(255,255,255,0.1) 2px,
        rgba(255,255,255,0.1) 4px
      )
    `,
    },
  },

  floorCheckeredPlates: {
    width: "100%",
    height: "100%",
    background: "linear-gradient(135deg, #6C7B7F, #8B9598)",
    position: "relative",
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: `
      repeating-linear-gradient(
        0deg,
        rgba(255,255,255,0.2) 0px,
        rgba(255,255,255,0.2) 1px,
        transparent 1px,
        transparent 8px
      ),
      repeating-linear-gradient(
        90deg,
        rgba(255,255,255,0.2) 0px,
        rgba(255,255,255,0.2) 1px,
        transparent 1px,
        transparent 8px
      )
    `,
      backgroundSize: "8px 8px",
    },
  },

  floorStoneClientScope: {
    width: "100%",
    height: "100%",
    background: "linear-gradient(135deg, #8D7B68, #A0916D, #8D7B68)",
    position: "relative",
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: `
      radial-gradient(circle at 20% 30%, rgba(255,255,255,0.3) 2px, transparent 2px),
      radial-gradient(circle at 70% 60%, rgba(0,0,0,0.2) 1px, transparent 1px),
      radial-gradient(circle at 40% 80%, rgba(255,255,255,0.2) 1px, transparent 1px)
    `,
      backgroundSize: "15px 15px, 20px 20px, 25px 25px",
    },
  },

  // Sub-sections
  subSection: {
    marginTop: "35px",
    marginBottom: "35px",
    padding: "25px",
    backgroundColor: "#f9f9fb",
    borderRadius: "14px",
    border: "1px solid #eaeaec",
  },
  subSectionTitle: {
    fontSize: "18px",
    fontWeight: "600",
    margin: "0 0 15px",
    color: "#333",
  },

  // Options row
  optionsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "15px",
    marginBottom: "15px",
  },
  optionButton: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "12px 16px",
    backgroundColor: "#f0f2f5",
    borderRadius: "10px",
    border: "1px solid transparent",
    cursor: "pointer",
    transition: "all 0.2s ease",
    position: "relative",
    minWidth: "120px",
    "&:hover": {
      backgroundColor: "#e8eaed",
      transform: "translateY(-2px)",
    },
  },
  selectedOption: {
    backgroundColor: "#e8f0fe",
    borderColor: "#007aff",
    boxShadow: "0 2px 8px rgba(0,122,255,0.15)",
    "&:hover": {
      backgroundColor: "#e0ecfe",
    },
  },
  optionIcon: {
    width: "24px",
    height: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "14px",
    color: "#007aff",
  },
  optionCheck: {
    position: "absolute",
    top: "8px",
    right: "8px",
    color: "#007aff",
    fontSize: "14px",
  },

  // Finish selectors
  finishSelector: {
    marginTop: "20px",
  },
  finishTitle: {
    fontSize: "15px",
    fontWeight: "500",
    margin: "0 0 12px",
    color: "#555",
  },
  finishOptions: {
    display: "flex",
    gap: "15px",
  },
  finishSwatch: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    cursor: "pointer",
    transition: "all 0.2s ease",
    border: "2px solid transparent",
    position: "relative",
    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
    "&:hover": {
      transform: "scale(1.1)",
    },
  },
  selectedFinish: {
    borderColor: "#007aff",
    boxShadow: "0 2px 8px rgba(0,122,255,0.3)",
  },
  finishCheck: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    color: "#fff",
    textShadow: "0 1px 2px rgba(0,0,0,0.3)",
  },

  // Finish materials
  stainlessFinish: {
    background: "linear-gradient(135deg, #b8b8b8, #e8e8e8, #d4d4d4)",
  },
  goldFinish: {
    background: "linear-gradient(135deg, #d4af37, #f9d776, #d4af37)",
  },
  woodFinish: {
    background: "linear-gradient(135deg, #8B4513, #A0522D, #8B4513)",
  },
  blackFinish: {
    background: "#111",
  },

  // Checkboxes
  checkboxGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
    gap: "15px",
  },
  checkboxItem: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "15px",
    backgroundColor: "#f5f7fa",
    borderRadius: "10px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    border: "1px solid transparent",
    "&:hover": {
      backgroundColor: "#eff1f5",
      transform: "translateY(-2px)",
    },
  },
  checkboxSelected: {
    backgroundColor: "#e8f0fe",
    borderColor: "#007aff",
  },
  checkbox: {
    width: "22px",
    height: "22px",
    borderRadius: "6px",
    border: "2px solid #007aff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#007aff",
    backgroundColor: "#fff",
    fontSize: "12px",
  },
  checkboxLabel: {
    display: "flex",
    flexDirection: "column",
  },
  checkboxTitle: {
    fontSize: "15px",
    fontWeight: "500",
    marginBottom: "4px",
  },
  checkboxDesc: {
    fontSize: "13px",
    color: "#666",
  },

  // Summary panel
  summaryPanel: {
    backgroundColor: "#f9f9fb",
    borderRadius: "14px",
    padding: "25px",
    marginBottom: "30px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
    border: "1px solid #eaeaec",
  },
  summaryTitle: {
    margin: "0 0 20px",
    fontSize: "18px",
    fontWeight: "600",
    color: "#333",
    borderBottom: "1px solid #eaeaec",
    paddingBottom: "10px",
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
    gap: "20px",
  },
  summaryItem: {
    display: "flex",
    flexDirection: "column",
    gap: "5px",
  },
  summaryLabel: {
    fontSize: "13px",
    color: "#666",
    fontWeight: "500",
  },
  summaryValue: {
    fontSize: "15px",
    fontWeight: "600",
    color: "#333",
  },

  // Navigation
  navigation: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "20px",
  },
  backButton: {
    padding: "14px 26px",
    borderRadius: "10px",
    border: "1px solid #d0d0d0",
    backgroundColor: "white",
    color: "#4d4d4d",
    fontSize: "15px",
    fontWeight: "500",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#f5f5f7",
      transform: "translateY(-2px)",
    },
  },
  nextButton: {
    padding: "14px 26px",
    borderRadius: "10px",
    border: "none",
    backgroundColor: "#007aff",
    color: "white",
    fontSize: "15px",
    fontWeight: "500",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    boxShadow: "0 4px 12px rgba(0,122,255,0.2)",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#0062cc",
      transform: "translateY(-2px)",
    },
  },

  // Preview notice
  previewNotice: {
    display: "flex",
    alignItems: "center",
    padding: "15px 20px",
    backgroundColor: "#f0f7ff",
    borderRadius: "12px",
    borderLeft: "4px solid #007aff",
  },
  noticeIcon: {
    color: "#007aff",
    fontSize: "22px",
    marginRight: "15px",
  },
  noticeText: {
    fontSize: "14px",
    color: "#333",
    margin: 0,
    lineHeight: 1.5,
  },

  // Responsive styles
  "@media (max-width: 768px)": {
    mainContent: {
      padding: "20px",
    },
    optionsGrid: {
      gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
    },
    summaryGrid: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
    optionsRow: {
      flexWrap: "wrap",
      justifyContent: "space-between",
    },
    optionButton: {
      flexBasis: "calc(50% - 10px)",
      justifyContent: "center",
      padding: "10px",
    },
    tabButton: {
      padding: "12px 8px",
    },
    finishOptions: {
      justifyContent: "space-around",
    },
  },
  "@media (max-width: 480px)": {
    mainContent: {
      padding: "15px",
    },
    tabsContainer: {
      flexWrap: "wrap",
      borderRadius: "10px",
    },
    tabButton: {
      minWidth: "50%",
      borderRadius: "0",
    },
    designVisual: {
      height: "120px",
    },
    optionsGrid: {
      gridTemplateColumns: "1fr",
    },
    summaryGrid: {
      gridTemplateColumns: "1fr",
    },
    navigation: {
      flexDirection: "column",
      gap: "15px",
    },
    backButton: {
      width: "100%",
      justifyContent: "center",
    },
    nextButton: {
      width: "100%",
      justifyContent: "center",
    },
    checkboxGrid: {
      gridTemplateColumns: "1fr",
    },
  },
};

export default DesignFinishSelector;
